import type { NetworkDashboardAvailableStore } from '@gozoki/api-types'
import { DateTime } from 'luxon'
import { comparisonShiftDays } from '~/components/dashboards/comparison'
import { resolveAllTimeStartIso } from '~/components/dashboards/filters/all-time-range'
import { getPresetForRange } from '~/components/dashboards/filters/date-presets'
import {
  DASHBOARD_ALL_TIME_START_ISO,
  type DashboardFiltersRowSelection,
  type NetworkDashboardDateRange,
} from '~/components/dashboards/filters/types'
import type { DateRangePreset } from '~/components/ui/date-range-picker'
import { resolveEffectiveStoreIds } from './effective-store-ids'

// Presets réutilisés du Network Dashboard (jeu complet — le picker RH n'affiche que
// DASHBOARD_DATE_PRESETS, mais l'URL peut porter n'importe quel slug standard).
export const HR_DATE_PRESETS = [
  'yesterday',
  'today',
  '7d',
  '30d',
  '3m',
  '12m',
  'mtd',
  'ytd',
  'all',
] as const satisfies readonly DateRangePreset[]

export interface HrDashboardRangeSearch {
  startDate?: string
  endDate?: string
  datePreset?: DateRangePreset
}

export interface ResolvedHrDashboardRange {
  /**
   * ISO 'YYYY-MM-DD' brut de l'URL. « Toujours » ⇒ DASHBOARD_ALL_TIME_START_ISO : à passer par
   * `hrPeriodStartIso` avant d'atteindre une query, jamais tel quel.
   */
  startIso: string
  /** ISO 'YYYY-MM-DD' envoyé aux queries (`dateEnd`). */
  endIso: string
  /** Forme attendue par `DateRangePicker` / `DashboardFilters` (`from: null` = « Toujours »). */
  range: NetworkDashboardDateRange
  /** Préset actif pour la surbrillance du picker, ou null pour une plage custom. */
  preset: DateRangePreset | null
}

/**
 * Plage par défaut du dashboard RH = SEMAINE PRÉCÉDENTE (lundi → dimanche), et non plus « 7 jours
 * glissants ». Luxon `startOf('week')` = lundi (ISO 8601). Spécifique au RH : le Network Dashboard
 * garde son défaut 7j (`getDefaultDateRange`).
 */
export function getHrDefaultRange(): { from: Date; to: Date } {
  const start = DateTime.now().startOf('week').minus({ weeks: 1 })
  const end = start.plus({ days: 6 })
  return { from: start.toJSDate(), to: end.toJSDate() }
}

/**
 * Libellé du badge de temporalité RH : « Semaine N » quand la plage est une semaine ISO complète
 * (lundi → dimanche), sinon `undefined` pour laisser `SelectionPeriodBadge` afficher « X jours
 * glissants ». Couvre le défaut (semaine précédente) et toute sélection d'une semaine pleine.
 */
export function hrDashboardPeriodLabel(startIso: string, endIso: string): string | undefined {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  if (start.weekday === 1 && end.weekday === 7 && end.diff(start, 'days').days === 6) {
    return `Semaine ${start.weekNumber}`
  }
  return undefined
}

/**
 * Sous-titre période + comparaison des modales dashboard RH (charte Réseau & Boutiques, cf.
 * network-dashboard-figures-legend) : « Chiffres du … au … - Comparaison avec les chiffres du …
 * au … ». La période comparée = période équivalente précédente (S-1), décalée d'une durée entière.
 */
const frDate = (d: DateTime) => d.toFormat('dd/MM/yyyy')

/**
 * Fragments du sous-titre période/comparaison RH : `period` = « Chiffres du … au … », et
 * `comparisonDates` = « du … au … » de la période comparée S-1 (période équivalente précédente,
 * décalée d'une durée entière). Partagé par `periodSubtitle` (chaîne plate) et `HrPeriodSubtitle`
 * (rendu JSX qui injecte le périmètre comparé dans la clause « Comparaison (…) »).
 */
export function hrPeriodParts(
  startIso: string,
  endIso: string,
): { period: string; comparisonDates: string } {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  const durationDays = Math.round(end.diff(start, 'days').days) + 1
  const shiftDays = comparisonShiftDays(durationDays)
  const prevStart = start.minus({ days: shiftDays })
  const prevEnd = end.minus({ days: shiftDays })
  return {
    period: `Chiffres du ${frDate(start)} au ${frDate(end)}`,
    comparisonDates: `du ${frDate(prevStart)} au ${frDate(prevEnd)}`,
  }
}

export function periodSubtitle(startIso: string, endIso: string): string {
  const { period, comparisonDates } = hrPeriodParts(startIso, endIso)
  return `${period} - Comparaison avec les chiffres ${comparisonDates}`
}

/**
 * Unité de la période de comparaison RH selon la durée choisie (charte Réseau & Boutiques, cf.
 * `formatNetworkDashboardComparisonUnitFr`) : ≤ 7j → S, 30j → M, 365j → A, sinon durée custom → C.
 * Le libellé porte le suffixe « -1 » (ex. « S-1 », « M-1 »). Un décalage de 7 jours vaut « S » que
 * la période fasse une semaine pleine ou moins de 6 jours (`comparisonShiftDays`) : dans les deux
 * cas la comparaison porte sur la semaine précédente, jamais sur « J-1 » ni « C-1 ».
 */
export function hrComparisonUnit(startIso: string, endIso: string): 'S' | 'M' | 'A' | 'C' {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  const durationDays = Math.round(end.diff(start, 'days').days) + 1
  if (comparisonShiftDays(durationDays) === 7) return 'S'
  if (start.plus({ days: 29 }).equals(end)) return 'M'
  if (start.plus({ days: 364 }).equals(end)) return 'A'
  return 'C'
}

/** Token « X-1 » de la période comparée (ex. « S-1 », « M-1 ») — sert aux légendes/séries/exports. */
export function hrComparisonToken(startIso: string, endIso: string): string {
  return `${hrComparisonUnit(startIso, endIso)}-1`
}

/** Libellé de patate « vs X-1 » (ex. « vs M-1 »). */
export function hrComparisonLabel(startIso: string, endIso: string): string {
  return `vs ${hrComparisonToken(startIso, endIso)}`
}

/**
 * Résout les params d'URL du dashboard RH (`startDate`/`endDate`/`datePreset`) en un range
 * concret. Défaut = semaine précédente (`getHrDefaultRange`).
 */
export function resolveHrDashboardRange(search: HrDashboardRangeSearch): ResolvedHrDashboardRange {
  const defaultRange = getHrDefaultRange()
  const startIso = search.startDate ?? DateTime.fromJSDate(defaultRange.from).toISODate()!
  const endIso = search.endDate ?? DateTime.fromJSDate(defaultRange.to).toISODate()!

  const range: NetworkDashboardDateRange = {
    from: startIso === DASHBOARD_ALL_TIME_START_ISO ? null : DateTime.fromISO(startIso).toJSDate(),
    to: DateTime.fromISO(endIso).toJSDate(),
  }

  return { startIso, endIso, range, preset: search.datePreset ?? getPresetForRange(range) }
}

/**
 * Début de période à envoyer à l'API. La sentinelle « Toujours » ne décrit pas une période ouverte :
 * envoyée telle quelle elle fait démarrer la période au début de Lucie (~2 400 jours) même pour une
 * boutique ouverte l'an dernier — moyennes par jour divisées par la période entière, unité de
 * comparaison faussée, comparaison qui tombe sur des années sans donnée. Elle est résolue en date de
 * première vente du périmètre EFFECTIF (géo ∩ ancienneté), donc des boutiques réellement
 * interrogées. Repli sur la sentinelle tant que la liste des boutiques n'est pas chargée : les
 * appelants attendent `hrPeriodQueriesEnabled` avant de lancer leurs requêtes.
 */
export function hrPeriodStartIso(
  startIso: string,
  endIso: string,
  availableStores: NetworkDashboardAvailableStore[],
  effectiveStoreIds: number[] | undefined,
): string {
  if (startIso !== DASHBOARD_ALL_TIME_START_ISO) {
    return startIso
  }
  return resolveAllTimeStartIso(
    availableStores,
    { storeIds: [], storeAgeFilter: 'default', ageStoreIds: effectiveStoreIds },
    endIso,
  )
}

/** Hors « Toujours » il n'y a rien à résoudre ; sinon il faut la liste des boutiques. */
export function hrPeriodQueriesEnabled(startIso: string, availableStoresSettled: boolean): boolean {
  return startIso !== DASHBOARD_ALL_TIME_START_ISO || availableStoresSettled
}

/**
 * Sélection du sélecteur réseau → params attendus par les endpoints de détail RH. Le filtre
 * ancienneté est résolu en liste d'ids (∩ sélection géo) exactement comme le dashboard, et le début
 * de période passe par `hrPeriodStartIso`. Partagé par les modales Effectif / Frais personnel /
 * FP-CA, qui doivent interroger EXACTEMENT le même périmètre.
 */
export function hrSelectionWindowParams(
  selection: DashboardFiltersRowSelection,
  availableStores: NetworkDashboardAvailableStore[],
): { dateStart: string; dateEnd: string; storeIds: number[] | undefined } {
  const { startDate, endDate } = serializeHrDashboardRange(
    selection.dateRange,
    selection.datePreset,
  )
  const storeIds = resolveEffectiveStoreIds(
    selection.storeIds,
    selection.storeAgeFilter,
    selection.ageStoreIds,
    availableStores,
    endDate,
  )
  return {
    dateStart: hrPeriodStartIso(startDate, endDate, availableStores, storeIds),
    dateEnd: endDate,
    storeIds,
  }
}

/**
 * Sérialise le range renvoyé par `DashboardFilters` vers les params d'URL du dashboard RH.
 * Miroir de `buildSingleSelectionSearch` (Réseau) restreint aux 3 clés RH. La sentinelle reste
 * portée par l'URL : c'est elle qui garde « Toujours » sélectionné dans le picker.
 */
export function serializeHrDashboardRange(
  dateRange: NetworkDashboardDateRange,
  datePreset: DateRangePreset | null | undefined,
): { startDate: string; endDate: string; datePreset: DateRangePreset | undefined } {
  return {
    startDate: dateRange.from
      ? DateTime.fromJSDate(dateRange.from).toISODate()!
      : DASHBOARD_ALL_TIME_START_ISO,
    endDate: DateTime.fromJSDate(dateRange.to).toISODate()!,
    datePreset: datePreset ?? undefined,
  }
}
